import { ShowModal } from '@citrite/web-ui-component/dist/typings/Modal/withModals';
import {
	hasFeatureCanary,
	UserAccountData,
	WorkspaceConfiguration,
} from '@citrite/workspace-ui-platform';
import { trackAnalyticsEvent } from 'analytics';
import { PowerState } from 'App/Activity/Network/ActionsUtil';
import {
	LaunchProgressStatusResult,
	TROUBLESHOOTING_STATUS,
} from 'App/ProgressAlert/utils';
import { isHomeScreen } from 'App/Screen/util';
import { shouldPromptLaunchInCEBPreference } from 'App/Tile/shouldPromptLaunchInCEBPreference';
import { environment } from 'Environment';
import { FeatureFlag } from 'Environment/FeatureFlag';
import { launchResource } from 'Environment/launchResource';
import { isNativeClient } from 'Environment/launchResource/device';
import {
	isLaunchResourceWithIcaSupported,
	LaunchParams,
	LaunchStatus,
} from 'Environment/launchResource/launchResource';
import {
	shouldNotShowPromptAgain,
	showInstallExtensionModal,
} from 'Workspace/BrowserExtension/InstallBrowserExtensionModal';
import {
	LaunchInCEBMode,
	launchSaaSApp,
	SaaSAppLaunchInCEBParams,
} from 'Workspace/CitrixEnterpriseBrowser';
import { ResourceContext } from 'Workspace/ResourceProvider';
import {
	DesktopAssignmentType,
	isSaasApp,
	Resource,
} from 'Workspace/ResourceProvider/resourceTypes';
import { AppsUserEventPayload } from 'Workspace/TelemetryEvents/appsEvent/AppsUserEventPayload';
import { DesktopsUserEventPayload } from 'Workspace/TelemetryEvents/desktopsEvent/DesktopsUserEventPayload';
import { EventSite } from 'Workspace/TelemetryEvents/TelemetryEventTypes';
import { UserDetails } from 'Workspace/UserContext';

const getEventSite = () => (isHomeScreen() ? EventSite.Recent : EventSite.Tile);

const captureAnalytics = (resource: Resource) => {
	const site = getEventSite();
	const eventPayload = resource.isdesktop
		? DesktopsUserEventPayload.launch(site, resource.type)
		: AppsUserEventPayload.launch(site, resource.type);
	trackAnalyticsEvent(eventPayload);
};

export const launchGenericResource = ({
	resource,
	resourceContext,
	workspaceConfiguration,
	troubleshoot = false,
	targetWindow,
}: LaunchParams) => {
	captureAnalytics(resource);
	launchResource({
		resource,
		resourceContext,
		workspaceConfiguration,
		troubleshoot,
		targetWindow,
	});
};

// allow-unused-export
export const getUserProfileIdentity = ({
	userId,
	authAlias,
	userDisplayName,
}: UserDetails) => {
	switch (true) {
		case !!userId:
			return { userId };
		case !!authAlias:
			return { authAlias };
		case !!userDisplayName:
			return { userName: userDisplayName };
		default:
			return {};
	}
};

/**
 * Requirements:
 * - Feature flag must be enabled
 * - Resource must support inspect connection
 * - For native clients: ICA support must be available
 * TODO: For Chrome App, capability exchange between WSUI and Chrome App is required to determine
 * if the current version supports inspect connection and to display the troubleshoot button.
 */
export const shouldShowTroubleshootOption = (
	resource: Resource,
	workspaceConfiguration: WorkspaceConfiguration
): boolean => {
	return (
		environment.citrixCloudConnected &&
		resource?.supportsinspectconnection &&
		hasFeatureCanary(workspaceConfiguration, FeatureFlag.EnableInspectConnection) &&
		(!isNativeClient() || isLaunchResourceWithIcaSupported())
	);
};

export interface LaunchResourceOnClickParams {
	workspaceConfiguration: WorkspaceConfiguration;
	resource: Resource;
	resourceContext: ResourceContext;
	userDetails: UserAccountData;
	canShowBrowserExtensionPrompt: boolean;
	showModal: ShowModal;
	targetWindow?: Window;
	troubleshoot?: boolean;
}

export const launchResourceOnClick = ({
	workspaceConfiguration,
	resource,
	resourceContext,
	userDetails,
	canShowBrowserExtensionPrompt,
	showModal,
	targetWindow,
	troubleshoot,
}: LaunchResourceOnClickParams) => {
	const isSaaSAppResource = isSaasApp(resource);
	const useCitrixEnterpriseBrowser = workspaceConfiguration?.pluginAssistant?.html5
		?.useCitrixEnterpriseBrowser as LaunchInCEBMode;
	const resumeResourceLaunch = () =>
		launchGenericResource({
			resource,
			resourceContext,
			workspaceConfiguration,
			troubleshoot,
			targetWindow,
		});
	const { workspaceWebExtensionPrompt } = workspaceConfiguration?.pluginAssistant?.html5;

	switch (true) {
		case isSaaSAppResource &&
			shouldPromptLaunchInCEBPreference(useCitrixEnterpriseBrowser):
			const params: SaaSAppLaunchInCEBParams = {
				showModal,
				onFallback: resumeResourceLaunch,
				resourceId: resource.id,
				mode: useCitrixEnterpriseBrowser,
				...getUserProfileIdentity(userDetails),
				receiverConfigurationDownloadUrl:
					workspaceConfiguration?.userInterface?.receiverConfiguration?.downloadURL,
			};
			launchSaaSApp(params);
			break;
		case !isSaaSAppResource &&
			canShowBrowserExtensionPrompt &&
			!shouldNotShowPromptAgain(workspaceWebExtensionPrompt):
			showInstallExtensionModal(resumeResourceLaunch, workspaceWebExtensionPrompt);
			break;
		default:
			launchGenericResource({
				resource,
				resourceContext,
				workspaceConfiguration,
				troubleshoot,
				targetWindow,
			});
			break;
	}
};

export const shouldCallResourceSessionsRefresh = (
	resource: Resource,
	workspaceConfiguration: WorkspaceConfiguration
): boolean => {
	const isMachinePowerStateEnabled = hasFeatureCanary(
		workspaceConfiguration,
		FeatureFlag.EnableMachinePowerState
	);
	const isDisconnectAndLogoutEnabled = hasFeatureCanary(
		workspaceConfiguration,
		FeatureFlag.EnableDisconnectAndLogout
	);

	if (!resource?.isdesktop) {
		return false;
	}

	if (!isMachinePowerStateEnabled && !isDisconnectAndLogoutEnabled) {
		return false;
	}

	// Machine power state is only for assigned and assign-on-first-use desktops, not shared ones
	// Skip refresh for shared desktops when only machine power state is enabled
	const isSharedDesktopWithDisconnectAndLogoutDisabled =
		isMachinePowerStateEnabled &&
		resource?.desktopassignmenttype === DesktopAssignmentType.Shared &&
		!isDisconnectAndLogoutEnabled;

	if (isSharedDesktopWithDisconnectAndLogoutDisabled) {
		return false;
	}

	return true;
};

export const isSharedResource = (resource: Resource): boolean => {
	return resource.desktopassignmenttype === DesktopAssignmentType.Shared;
};

export type DesktopOSType = 'Windows' | 'macOS' | 'Linux' | 'Unknown';
type DesktopState =
	| PowerState.LOADING
	| PowerState.UNAVAILABLE
	| 'UnderMaintenance'
	| 'Active';

export type DesktopWallpaper = DesktopOSType | Exclude<DesktopState, 'Active'>;

export const mapOSNameToType = (osName?: string): DesktopOSType => {
	if (!osName) {
		return 'Unknown';
	}

	const osRegexMap: { regex: RegExp; type: DesktopOSType }[] = [
		{ regex: /\b(windows|microsoft|win)\b/i, type: 'Windows' },
		{ regex: /\b(mac|darwin|osx|os ?x|mac ?os)\b/i, type: 'macOS' },
		{
			regex: /\b(linux|ubuntu|red ?hat|centos|debian|fedora|suse|mint)\b/i,
			type: 'Linux',
		},
	];

	const matchedOS = osRegexMap.find(({ regex }) => regex.test(osName));
	return matchedOS?.type || 'Unknown';
};

export const getDesktopWallpaper = (desktopOsState?: DesktopWallpaper): string => {
	const desktopOSImages: Record<DesktopWallpaper, string> = {
		Windows: require('styles/img/window-wallpaper.png'),
		macOS: require('styles/img/mac-wallpaper.png'),
		Linux: require('styles/img/linux-wallpaper.png'),
		Unknown: require('styles/img/unknown-wallpaper.png'),
		Loading: require('styles/img/loading-wallpaper.png'),
		Unavailable: require('styles/img/unavailable-wallpaper.png'),
		UnderMaintenance: require('styles/img/unavailable-wallpaper.png'),
	};

	return desktopOSImages[desktopOsState || 'Unknown'];
};

export function isLaunchStatus(value: string): value is LaunchProgressStatusResult {
	if (value === TROUBLESHOOTING_STATUS) {
		return true;
	}
	return Object.values(LaunchStatus).includes(value as LaunchStatus);
}

export function removeEllipsis(text: string): string {
	return text.replace('...', '');
}
